import { motion } from "framer-motion";
import { BentoCard } from "@/components/BentoCard";
import { Stepper } from "@/components/materials/Stepper";
import { formatZAR } from "@/store/dashboardStore";
import { formatQty, itemCost, selectMaterialsTotal, useMaterialsStore } from "@/store/materialsStore";

const EASE = [0.22, 1, 0.36, 1] as const;
const TH = "px-3 py-3 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-cad sm:px-6";

export function MaterialsTable() {
  const items = useMaterialsStore((s) => s.items);
  const total = useMaterialsStore(selectMaterialsTotal);
  const recalc = useMaterialsStore((s) => s.recalculateMaterialItem);

  return (
    <BentoCard data-testid="materials-table-card">
      <table data-testid="materials-table" className="w-full border-collapse">
        <thead>
          <tr className="border-b border-cad/25">
            <th className={TH}>Item</th>
            <th className={`${TH} hidden md:table-cell`}>Category</th>
            <th className={`${TH} text-center`}>Quantity</th>
            <th className={`${TH} hidden text-right sm:table-cell`}>Cost</th>
          </tr>
        </thead>
        <tbody>
          {items.map((m, i) => {
            const cost = formatZAR(Math.round(itemCost(m)));
            return (
              <motion.tr
                key={m.id}
                data-testid={`material-row-${m.id}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.05, duration: 0.5, ease: EASE }}
                className="border-b border-cad/15 transition-colors duration-200 hover:bg-white/[0.025]"
              >
                <td className="px-3 py-3.5 sm:px-6">
                  <div className="flex flex-col gap-0.5">
                    <span className="font-sans text-sm font-medium text-white">{m.name}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-cad md:hidden">{m.category}</span>
                    <span className="mt-0.5 font-mono text-xs tabular-nums text-safety sm:hidden">{cost}</span>
                  </div>
                </td>
                <td className="hidden px-6 py-3.5 md:table-cell">
                  <span className="rounded-md border border-cad/40 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-white/60">{m.category}</span>
                </td>
                <td className="px-3 py-3.5 sm:px-6">
                  <div className="flex justify-center">
                    <Stepper
                      testId={`stepper-${m.id}`}
                      value={formatQty(m)}
                      unit={m.unit}
                      disabledDec={m.quantity <= 0}
                      onDec={() => recalc(m.id, m.quantity - m.step)}
                      onInc={() => recalc(m.id, m.quantity + m.step)}
                    />
                  </div>
                </td>
                <td className="hidden px-6 py-3.5 text-right sm:table-cell">
                  <div className="flex flex-col items-end gap-0.5">
                    <motion.span key={cost} data-testid={`material-cost-${m.id}`} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} className="font-mono text-sm tabular-nums text-white">
                      {cost}
                    </motion.span>
                    <span className="font-mono text-[9px] tracking-[0.12em] text-cad">@ {formatZAR(m.unitCost)}/{m.unit}</span>
                  </div>
                </td>
              </motion.tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="bg-safety/[0.04]">
            <td className="px-3 py-4 font-mono text-[10px] uppercase tracking-[0.2em] text-cad sm:hidden">Estimated total</td>
            <td colSpan={2} className="hidden px-6 py-4 font-mono text-[10px] uppercase tracking-[0.2em] text-cad sm:table-cell md:hidden">Estimated total</td>
            <td colSpan={3} className="hidden px-6 py-4 font-mono text-[10px] uppercase tracking-[0.2em] text-cad md:table-cell">Estimated total · {items.length} line items</td>
            <td className="px-3 py-4 text-right sm:px-6">
              <span data-testid="materials-total" className="font-display text-xl font-bold tabular-nums text-safety sm:text-2xl">{formatZAR(Math.round(total))}</span>
            </td>
          </tr>
        </tfoot>
      </table>
    </BentoCard>
  );
}
